/**
 * Shared auth service for Platzona platform frontends.
 * Uses VITE_AUTH_SERVICE_URL when run in a Vite project.
 */

/** Read Vite env variables without an `as any` cast. */
function viteEnv(): Record<string, string | undefined> {
  if (typeof import.meta === 'undefined') return {};
  return (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
}

const AUTH_EVENT = 'platzona-auth-change';
const TOKEN_KEY = 'auth_token';
const REFRESH_KEY = 'auth_refresh_token';
const USER_KEY = 'auth_user';
/**
 * ADR-044 (owner 2026-10-27): "same browser = same device after a re-login". The device secret the server gave this
 * browser; sent with the next login only if the app allows it (its guard - Platzel checks that this device's encryption
 * keys are still here). Kept when a session merely expires; deleted on "Abmelden" (the server revokes that device anyway).
 */
const REATTACH_KEY = 'auth_device_reattach';

export interface DeviceReattachRecord {
  userId: string;
  deviceId: string;
  secret: string;
}

let reattachGuard: ((rec: DeviceReattachRecord) => Promise<boolean>) | null = null;

function readReattachRecord(): DeviceReattachRecord | null {
  try {
    const r = JSON.parse(localStorage.getItem(REATTACH_KEY) || 'null') as Partial<DeviceReattachRecord> | null;
    return r && typeof r.userId === 'string' && typeof r.deviceId === 'string' && typeof r.secret === 'string' ? (r as DeviceReattachRecord) : null;
  } catch {
    return null;
  }
}

/** The reattach part of a login request - only when enabled, a record exists and the app's guard agrees. */
async function reattachForLogin(): Promise<{ deviceId: string; secret: string } | undefined> {
  if (!reattachGuard) return undefined;
  const rec = readReattachRecord();
  if (!rec) return undefined;
  try {
    return (await reattachGuard(rec)) ? { deviceId: rec.deviceId, secret: rec.secret } : undefined;
  } catch {
    return undefined;
  }
}

function storeReattach(data: Record<string, unknown>): void {
  if (!reattachGuard) return;
  const r = data.deviceReattach as { deviceId?: unknown; secret?: unknown } | undefined;
  const u = data.user as { _id?: unknown; id?: unknown } | undefined;
  const userId = String(u?._id ?? u?.id ?? '');
  if (!r || typeof r.deviceId !== 'string' || typeof r.secret !== 'string' || !userId) return;
  try {
    localStorage.setItem(REATTACH_KEY, JSON.stringify({ userId, deviceId: r.deviceId, secret: r.secret }));
  } catch {
    /* private mode */
  }
}

/**
 * Portal origin used for cross-platform SSO via postMessage.
 *
 * - Prefer `VITE_PORTAL_ORIGIN` when Portal is hosted elsewhere.
 * - Fallback to `http://{hostname}:3000` for local/LAN dev.
 */
export function getPortalOrigin(): string {
  const raw = viteEnv().VITE_PORTAL_ORIGIN;
  const env = raw ? String(raw).trim() : '';
  const safe = resolveSafeBaseUrl(env);
  if (safe) return safe;
  if (typeof window === 'undefined') return '';
  return `http://${window.location.hostname}:3000`;
}

function isLocalOrPrivateHostname(host: string): boolean {
  return host === 'localhost' ||
    host === '127.0.0.1' ||
    /^(192\.168|10\.|172\.(1[6-9]|2\d|3[01]))/.test(host);
}

/**
 * True when the browser hostname is a user custom domain (not a known platform app host).
 * Pass production app hostnames, e.g. `['localhost', '127.0.0.1', 'web.platzona.ai']`.
 */
export function isCustomDomainBrowserHost(knownAppHosts: readonly string[]): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.toLowerCase();
  const known = new Set(knownAppHosts.map((h) => h.toLowerCase()));
  if (known.has(host)) return false;
  if (isLocalOrPrivateHostname(host)) return false;
  return true;
}

export function resolveSafeBaseUrl(raw: string): string {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('/')) return trimmed.replace(/\/$/, '');
  try {
    const u = new URL(trimmed);
    return isLocalOrPrivateHostname(u.hostname) ? trimmed.replace(/\/$/, '') : '';
  } catch {
    return '';
  }
}

export type PortalTokenHandoffOptions = {
  /**
   * Query param flag used to enable the handoff. Defaults to `from_portal=1`.
   */
  queryParamKey?: string;
  /**
   * If true, production handoff defaults to same-origin unless `VITE_PORTAL_ORIGIN` is explicitly provided.
   * Intended for offline-first deployments (e.g. Tradalous).
   */
  offlineFirstProdSameOrigin?: boolean;
};

export function openedFromPortal(opts: Pick<PortalTokenHandoffOptions, 'queryParamKey'> = {}): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const key = opts.queryParamKey ?? 'from_portal';
  return params.get(key) === '1';
}

export function getPortalDashboardUrl(opts: Pick<PortalTokenHandoffOptions, 'offlineFirstProdSameOrigin'> = {}): string {
  if (typeof window === 'undefined') return '';
  const host = window.location.hostname;
  const isLocal = isLocalOrPrivateHostname(host);
  if (isLocal) return `${getPortalOrigin()}/dashboard`;
  if (opts.offlineFirstProdSameOrigin && !viteEnv().VITE_PORTAL_ORIGIN) {
    return '/dashboard';
  }
  return `${getPortalOrigin()}/dashboard`;
}

/**
 * Setup Portal → platform SSO token handoff using `window.postMessage`.
 *
 * Returns a cleanup function to remove the event listener.
 */
export function setupPortalTokenHandoff(opts: PortalTokenHandoffOptions = {}): () => void {
  if (typeof window === 'undefined') return () => {};

  const params = new URLSearchParams(window.location.search);
  const key = opts.queryParamKey ?? 'from_portal';
  if (params.get(key) !== '1' || !window.opener) return () => {};

  // Drop stale platform tokens so APIs don't fire with an old session before handoff.
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);

  const host = window.location.hostname;
  const isLocal = isLocalOrPrivateHostname(host);

  const portalOrigin = isLocal
    ? getPortalOrigin()
    : (opts.offlineFirstProdSameOrigin && !viteEnv().VITE_PORTAL_ORIGIN)
      ? window.location.origin
      : getPortalOrigin();

  const handler = (event: MessageEvent) => {
    if (event.origin !== portalOrigin) return;
    if (event.data?.type === 'PLATZONA_TOKEN' && event.data?.accessToken) {
      const refreshToken = typeof event.data.refreshToken === 'string' ? event.data.refreshToken : undefined;
      authService.setTokenFromPortal(event.data.accessToken, event.data.user || {}, refreshToken);
      window.removeEventListener('message', handler);
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }
  };

  window.addEventListener('message', handler);
  window.opener.postMessage({ type: 'PLATZONA_REQUEST_TOKEN' }, portalOrigin);

  return () => window.removeEventListener('message', handler);
}

/**
 * Wait for Portal SSO token handoff when opened with `?from_portal=1`.
 * Resolves when a token arrives or after `timeoutMs` (whichever comes first).
 */
export function waitForPortalTokenHandoff(timeoutMs = 5000): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  const params = new URLSearchParams(window.location.search);
  if (params.get('from_portal') !== '1' || !window.opener) return Promise.resolve();

  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener('platzona-auth-change', onAuth);
      clearTimeout(timer);
      resolve();
    };

    const onAuth = () => {
      if (localStorage.getItem(TOKEN_KEY)) finish();
    };

    window.addEventListener('platzona-auth-change', onAuth);
    const timer = setTimeout(finish, timeoutMs);
  });
}

function getAuthUrl(): string {
  const serviceUrl = viteEnv().VITE_AUTH_SERVICE_URL;
  if (serviceUrl) return resolveSafeBaseUrl(serviceUrl);
  return '';
}

function getPrimaryAuthBasePath(): string {
  const basePath = viteEnv().VITE_AUTH_BASE_PATH;
  if (basePath) {
    const v = String(basePath).trim();
    if (v) return v.startsWith('/') ? v : `/${v}`;
  }
  return '/api/auth';
}

function buildAuthUrl(basePath: string, endpoint: string): string {
  return `${getAuthUrl()}${basePath}${endpoint}`;
}

async function authFetch(endpoint: string, init: RequestInit): Promise<Response> {
  const primaryBasePath = getPrimaryAuthBasePath();
  const res = await fetch(buildAuthUrl(primaryBasePath, endpoint), init);
  // Backward compatibility: some environments expose auth under /api/v1/auth only.
  if (!res.ok && res.status === 404 && primaryBasePath !== '/api/v1/auth') {
    return fetch(buildAuthUrl('/api/v1/auth', endpoint), init);
  }
  return res;
}

/** Parse JSON body; empty or invalid bodies become {} so callers never throw from res.json(). */
async function readJsonBody(res: Response): Promise<Record<string, unknown>> {
  const text = await res.text();
  const trimmed = text.trim();
  if (!trimmed) return {};
  try {
    return JSON.parse(trimmed) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function strField(data: Record<string, unknown>, key: string, fallback: string): string {
  const v = data[key];
  if (typeof v === 'string' && v) return v;
  // express-validator returns { errors: [{msg:'...'}] } on validation failures
  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const first = data.errors[0] as Record<string, unknown>;
    const msg = first?.msg ?? first?.message;
    if (typeof msg === 'string' && msg) return msg;
  }
  return fallback;
}

// ─── Registration minimum age (16+) ───────────────────────────────────────────
// Client-side mirror of global-services/auth-service/src/utils/age.ts — UX only;
// the auth-service validator is the authoritative check. Keep the two in sync.

export const MIN_REGISTRATION_AGE = 16; // owner 2026-10-26 (was 13) - same as the auth service

export type DateOfBirthProblem = 'invalid' | 'future' | 'underage';

/** Today's UTC date as YYYY-MM-DD — use as the `max` of a date-of-birth <input type="date">. */
export function todayIsoDate(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** null when `value` (YYYY-MM-DD) is a real date and the person is at least 16 today (UTC). */
export function validateRegistrationDateOfBirth(value: string, now: Date = new Date()): DateOfBirthProblem | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return 'invalid';
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(Date.UTC(year, month - 1, day));
  if (year < 1900 || d.getUTCFullYear() !== year || d.getUTCMonth() !== month - 1 || d.getUTCDate() !== day) {
    return 'invalid';
  }
  if (d.getTime() > Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())) return 'future';
  const nowMonth = now.getUTCMonth() + 1;
  const beforeBirthday = nowMonth < month || (nowMonth === month && now.getUTCDate() < day);
  const age = now.getUTCFullYear() - year - (beforeBirthday ? 1 : 0);
  return age < MIN_REGISTRATION_AGE ? 'underage' : null;
}

/** Mirrors auth-service registerValidators: max length for first/last name (after trim). */
export const MAX_REGISTRATION_NAME_LENGTH = 100;

/** True when `value` is a usable required name: non-blank after trim and within the length limit. */
export function isValidRegistrationName(value: string): boolean {
  const name = value.trim();
  return name.length > 0 && name.length <= MAX_REGISTRATION_NAME_LENGTH;
}

/** UI language the user is registering in (shared @platzona/i18n storage key); used for auth emails. */
export function currentUiLanguage(): string | undefined {
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('platzona_language') : null;
    const lang = stored || (typeof document !== 'undefined' ? document.documentElement.lang : '') || '';
    return /^[A-Za-z]{2,3}([-_][A-Za-z0-9]{2,8})?$/.test(lang) ? lang : undefined;
  } catch {
    return undefined;
  }
}

/** Extra fields sent with registration (all required by auth-service). */
export interface RegistrationDetails {
  /** YYYY-MM-DD; validated server-side (16+) and never stored. */
  dateOfBirth: string;
  firstName: string;
  lastName: string;
  /** Must equal the password exactly; checked server-side, never stored. */
  confirmPassword: string;
}

// ─── Exported Error Types ──────────────────────────────────────────────────────

/** Thrown by auth methods on non-OK HTTP responses. Carries server-side error metadata. */
export class AuthApiError extends Error {
  public readonly status: number;
  public readonly rateLimited: boolean;
  public readonly retryAfterSeconds: number | undefined;
  public readonly attemptsRemaining: number | undefined;
  public readonly expired: boolean;
  public readonly tooManyAttempts: boolean;

  constructor(
    message: string,
    status: number,
    opts: {
      rateLimited?: boolean;
      retryAfterSeconds?: number;
      attemptsRemaining?: number;
      expired?: boolean;
      tooManyAttempts?: boolean;
    } = {},
  ) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
    this.rateLimited = opts.rateLimited ?? false;
    this.retryAfterSeconds = opts.retryAfterSeconds;
    this.attemptsRemaining = opts.attemptsRemaining;
    this.expired = opts.expired ?? false;
    this.tooManyAttempts = opts.tooManyAttempts ?? false;
  }
}

/** Thrown by register() — account created but email verification required. */
export class RequiresVerificationError extends Error {
  public readonly email: string;
  /** Dev-mode plaintext verification code (only set when EMAIL_LIVE=false). */
  public readonly devCode?: string;
  constructor(email: string, devCode?: string) {
    super('Account created! Please verify your email to activate your account.');
    this.name = 'RequiresVerificationError';
    this.email = email;
    this.devCode = devCode;
  }
}

/** Thrown by login() — credentials correct but email not yet verified. */
export class EmailNotVerifiedError extends Error {
  public readonly email: string;
  public readonly maskedEmail: string;
  constructor(email: string, maskedEmail: string) {
    super('Please verify your email address before signing in.');
    this.name = 'EmailNotVerifiedError';
    this.email = email;
    this.maskedEmail = maskedEmail;
  }
}

/** One signed-in device (change report 23). */
export interface AuthDevice {
  id: string;
  kind: 'browser' | 'pwa' | 'android' | 'ios';
  /** Website of the login, e.g. "platzel.de" (report 27); "unknown" for logins from before. */
  platform?: string;
  name: string;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  current: boolean;
}

/** Pre-device-management sessions are shown as one entry with this id. */
export const LEGACY_DEVICE_ID = 'legacy';

/**
 * Thrown by login() when the account already has the maximum number of devices. The password was correct:
 * `removalToken` lets the login screen remove one device (removeDeviceForLogin) and then retry the login.
 */
export class DeviceLimitError extends Error {
  public readonly maxDevices: number;
  public readonly devices: AuthDevice[];
  public readonly legacySessions: number;
  public readonly removalToken: string;
  constructor(maxDevices: number, devices: AuthDevice[], legacySessions: number, removalToken: string) {
    super('Too many devices are signed in.');
    this.name = 'DeviceLimitError';
    this.maxDevices = maxDevices;
    this.devices = devices;
    this.legacySessions = legacySessions;
    this.removalToken = removalToken;
  }
}

/** 'pwa' when running as an installed app, otherwise 'browser' (only used for the device list). */
function currentDeviceKind(): 'pwa' | 'browser' {
  try {
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    return standalone ? 'pwa' : 'browser';
  } catch {
    return 'browser';
  }
}

// ─── Shared Types ─────────────────────────────────────────────────────────────

export interface AuthUser {
  _id?: string;
  id?: string;
  username?: string;
  email?: string;
  roles?: string[];
  isEmailVerified?: boolean;
}

function normalizeAuthUser(raw: unknown): AuthUser {
  const u = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const id = u._id ?? u.id;
  const idStr = id != null ? String(id) : undefined;
  // Keep both shapes: many platforms read `.id`, auth payloads often only have `_id`.
  return {
    _id: idStr,
    id: idStr,
    username: typeof u.username === 'string' ? u.username : undefined,
    email: typeof u.email === 'string' ? u.email : undefined,
    roles: Array.isArray(u.roles) ? (u.roles as string[]) : undefined,
    isEmailVerified: u.isEmailVerified === true,
  };
}

function persistAuthUser(raw: unknown): void {
  const user = normalizeAuthUser(raw);
  if (user._id || user.username || user.email) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
}

export interface AuthService {
  login(emailOrUsername: string, password: string): Promise<string>;
  register(email: string, username: string, password: string, details: RegistrationDetails): Promise<void>;
  verifyEmail(email: string, code: string): Promise<string>;
  /** Returns the dev-mode plaintext code (only set when EMAIL_LIVE=false), if any. */
  resendVerification(email: string): Promise<string | undefined>;
  /** Send a password reset code to the given email. Returns the dev-mode plaintext code (only set when EMAIL_LIVE=false), if any. */
  forgotPassword(email: string): Promise<string | undefined>;
  /** Apply the reset code and set a new password. */
  /** Verify a password-reset code without consuming it. Throws on invalid/expired/too-many-attempts. */
  verifyResetCode(email: string, code: string): Promise<void>;
  resetPassword(email: string, code: string, newPassword: string): Promise<void>;
  /** Send a username reminder to the given email. */
  forgotUsername(email: string): Promise<void>;
  /** Look up the masked email associated with a username. */
  findEmail(username: string): Promise<string>;
  /** Live prefix-search: return usernames + masked emails matching query. */
  suggestEmail(q: string): Promise<{ username: string; maskedEmail: string }[]>;
  getToken(): string | null;
  getUser(): AuthUser | null;
  isAuthenticated(): boolean;
  logout(): Promise<void>;
  /**
   * ADR-044: after this, a login in this browser asks for the same device again when `guard` agrees (opt-in per app).
   */
  enableDeviceReattach(guard: (rec: DeviceReattachRecord) => Promise<boolean>): void;
  setTokenFromPortal(accessToken: string, user: AuthUser, refreshToken?: string): void;
  /** Attempt a silent token refresh. Returns the new access token or null. */
  silentRefresh(): Promise<string | null>;
  /** Fetch /me and persist auth_user when missing (legacy sessions). */
  hydrateUser(): Promise<AuthUser | null>;
  /** Permanently delete the authenticated account (requires password). */
  deleteAccount(password: string): Promise<void>;
  /** Signed-in devices of this account (report 23). */
  listDevices(): Promise<{ devices: AuthDevice[]; legacySessions: number; maxDevices: number }>;
  renameDevice(deviceId: string, name: string): Promise<void>;
  /** Signs one device out (LEGACY_DEVICE_ID = all sessions from before device management). */
  removeDevice(deviceId: string): Promise<void>;
  /** Signs out every device except this one. */
  removeOtherDevices(): Promise<number>;
  /** After a DeviceLimitError: removes one device without being signed in. */
  removeDeviceForLogin(removalToken: string, deviceId: string): Promise<void>;
}

// ─── Token expiry helpers ──────────────────────────────────────────────────────

/** Decode JWT payload without verifying signature (client-side only). */
export function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const base64 = token.split('.')[1];
    if (!base64) return null;
    // base64url without padding: add exactly the missing "=" (0, 2 or 1). The old '==='.slice(...) added one "="
    // where two were needed (and vice versa), so atob threw for about a third of all tokens.
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

/** Returns milliseconds until the token expires (negative if already expired). */
export function msUntilExpiry(token: string): number {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return 0;
  return payload.exp * 1000 - Date.now();
}

// ─── Proactive refresh scheduler ──────────────────────────────────────────────

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleProactiveRefresh(token: string): void {
  if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
  const ms = msUntilExpiry(token) - 2 * 60 * 1000; // 2 minutes before expiry
  if (ms <= 0) return;
  refreshTimer = setTimeout(async () => {
    await authService.silentRefresh();
  }, ms);
}

// ─── In-flight deduplication for concurrent refresh calls ────────────────────

/** Serialises token refreshes across tabs of the same site (Web Locks API; runs unlocked where unavailable). */
function withCrossTabRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManager }).locks : undefined;
  if (!locks?.request) return fn();
  return locks.request('platzona-auth-refresh', fn) as Promise<T>;
}

/** Authenticated call to the device endpoints; refreshes once on 401. */
async function deviceRequest(endpoint: string, init: RequestInit, fallbackError: string): Promise<Record<string, unknown>> {
  const send = (token: string | null) => authFetch(endpoint, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  let res = await send(localStorage.getItem(TOKEN_KEY));
  if (res.status === 401) {
    const fresh = await authService.silentRefresh();
    if (fresh) res = await send(fresh);
  }
  const data = await readJsonBody(res);
  if (!res.ok) throw new AuthApiError(strField(data, 'error', fallbackError), res.status);
  return data;
}

let refreshInFlight: Promise<string | null> | null = null;

// ─── Implementation ───────────────────────────────────────────────────────────

export const authService: AuthService = {
  /**
   * Login. Throws EmailNotVerifiedError if the account exists but the email
   * hasn't been verified yet. Callers should display a verification step.
   */
  async login(emailOrUsername: string, password: string): Promise<string> {
    const res = await authFetch('/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emailOrUsername, password, deviceKind: currentDeviceKind(), reattach: await reattachForLogin() }),
    });
    const data = await readJsonBody(res);

    if (!res.ok) {
      if (res.status === 409 && data.code === 'DEVICE_LIMIT') {
        throw new DeviceLimitError(
          typeof data.maxDevices === 'number' ? data.maxDevices : 10,
          Array.isArray(data.devices) ? (data.devices as AuthDevice[]) : [],
          typeof data.legacySessions === 'number' ? data.legacySessions : 0,
          strField(data, 'removalToken', ''),
        );
      }
      if (data.requiresEmailVerification) {
        throw new EmailNotVerifiedError(
          strField(data, 'email', emailOrUsername),
          strField(data, 'maskedEmail', ''),
        );
      }
      const headerVal = res.headers.get('Retry-After');
      const msg =
        strField(data, 'error', '') ||
        (res.status === 0 ? 'Network error — check connection and auth service URL.' : '') ||
        res.statusText ||
        'Login failed';
      throw new AuthApiError(msg, res.status, {
        rateLimited: res.status === 429,
        retryAfterSeconds: res.status === 429
          ? (headerVal ? Math.ceil(Number(headerVal)) : (typeof data.retryAfterSeconds === 'number' ? data.retryAfterSeconds : 60))
          : undefined,
        attemptsRemaining: typeof data.attemptsRemaining === 'number' ? data.attemptsRemaining : undefined,
      });
    }

    const accessToken = strField(data, 'accessToken', '');
    const refreshToken = strField(data, 'refreshToken', '');
    if (!accessToken || !refreshToken) {
      throw new AuthApiError('Invalid response from server (missing tokens).', res.status);
    }
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_KEY, refreshToken);
    if (data.user) persistAuthUser(data.user);
    storeReattach(data);
    scheduleProactiveRefresh(accessToken);
    window.dispatchEvent(new CustomEvent(AUTH_EVENT));
    return accessToken;
  },

  async register(email: string, username: string, password: string, details: RegistrationDetails): Promise<void> {
    const { dateOfBirth, firstName, lastName, confirmPassword } = details;
    const res = await authFetch('/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, username, password, confirmPassword, dateOfBirth, firstName: firstName.trim(), lastName: lastName.trim(), language: currentUiLanguage() }),
    });
    const data = await readJsonBody(res);

    if (!res.ok) {
      const headerVal = res.headers.get('Retry-After');
      throw new AuthApiError(strField(data, 'error', 'Registration failed'), res.status, {
        rateLimited: res.status === 429,
        retryAfterSeconds: res.status === 429
          ? (headerVal ? Math.ceil(Number(headerVal)) : (typeof data.retryAfterSeconds === 'number' ? data.retryAfterSeconds : 60))
          : undefined,
      });
    }

    // Backend always returns requiresVerification:true (no tokens on register)
    throw new RequiresVerificationError(
      strField(data, 'email', email),
      typeof data.devCode === 'string' ? data.devCode : undefined
    );
  },

  /**
   * Verify email with 6-digit code. Returns the access token on success.
   * Stores auth state in localStorage and dispatches auth event.
   */
  async verifyEmail(email: string, code: string): Promise<string> {
    const res = await authFetch('/verify-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    const data = await readJsonBody(res);

    if (!res.ok) {
      throw new AuthApiError(strField(data, 'error', 'Verification failed'), res.status, {
        expired: data.expired === true,
        tooManyAttempts: data.tooManyAttempts === true,
        attemptsRemaining: typeof data.attemptsRemaining === 'number' ? data.attemptsRemaining : undefined,
      });
    }

    const accessToken = strField(data, 'accessToken', '');
    const refreshToken = strField(data, 'refreshToken', '');
    if (!accessToken || !refreshToken) {
      throw new AuthApiError('Invalid response from server (missing tokens).', res.status);
    }
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_KEY, refreshToken);
    if (data.user) persistAuthUser(data.user);
    storeReattach(data);
    scheduleProactiveRefresh(accessToken);
    window.dispatchEvent(new CustomEvent(AUTH_EVENT));
    return accessToken;
  },

  /** Request a new verification code for the given email. */
  async resendVerification(email: string): Promise<string | undefined> {
    const res = await authFetch('/resend-verification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await readJsonBody(res);
    if (!res.ok) {
      const headerVal = res.headers.get('Retry-After');
      throw new AuthApiError(strField(data, 'error', 'Failed to resend code'), res.status, {
        rateLimited: res.status === 429,
        retryAfterSeconds: res.status === 429
          ? (headerVal ? Math.ceil(Number(headerVal)) : (typeof data.retryAfterSeconds === 'number' ? data.retryAfterSeconds : 60))
          : undefined,
      });
    }
    return typeof data.devCode === 'string' ? data.devCode : undefined;
  },

  async forgotPassword(email: string): Promise<string | undefined> {
    const res = await authFetch('/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await readJsonBody(res);
    if (!res.ok) {
      const headerVal = res.headers.get('Retry-After');
      throw new AuthApiError(strField(data, 'error', 'Failed to send reset code'), res.status, {
        rateLimited: res.status === 429,
        retryAfterSeconds: res.status === 429
          ? (headerVal ? Math.ceil(Number(headerVal)) : (typeof data.retryAfterSeconds === 'number' ? data.retryAfterSeconds : 60))
          : undefined,
      });
    }
    return typeof data.devCode === 'string' ? data.devCode : undefined;
  },

  async verifyResetCode(email: string, code: string): Promise<void> {
    const res = await authFetch('/verify-reset-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    const data = await readJsonBody(res);
    if (!res.ok) {
      throw new AuthApiError(strField(data, 'error', 'Invalid reset code'), res.status, {
        expired: data.expired === true,
        tooManyAttempts: data.tooManyAttempts === true,
        attemptsRemaining: typeof data.attemptsRemaining === 'number' ? data.attemptsRemaining : undefined,
      });
    }
  },

  async resetPassword(email: string, code: string, newPassword: string): Promise<void> {
    const res = await authFetch('/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, newPassword }),
    });
    const data = await readJsonBody(res);
    if (!res.ok) {
      throw new AuthApiError(strField(data, 'error', 'Password reset failed'), res.status, {
        expired: data.expired === true,
        tooManyAttempts: data.tooManyAttempts === true,
        attemptsRemaining: typeof data.attemptsRemaining === 'number' ? data.attemptsRemaining : undefined,
      });
    }
  },

  async forgotUsername(email: string): Promise<void> {
    const res = await authFetch('/forgot-username', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    const data = await readJsonBody(res);
    if (!res.ok) {
      throw new AuthApiError(strField(data, 'error', 'Failed to send username reminder'), res.status);
    }
  },

  async findEmail(username: string): Promise<string> {
    const res = await authFetch('/find-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });
    const data = await readJsonBody(res);
    if (!res.ok) {
      throw new AuthApiError(strField(data, 'error', 'No account found with that username'), res.status);
    }
    return strField(data, 'email', '');
  },

  async suggestEmail(q: string): Promise<{ username: string; maskedEmail: string }[]> {
    if (!q || q.length < 2) return [];
    const res = await authFetch(`/suggest-email?q=${encodeURIComponent(q)}`, {});
    const data = await readJsonBody(res);
    if (!res.ok) return [];
    return Array.isArray(data.suggestions) ? data.suggestions : [];
  },

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  getUser(): AuthUser | null {
    const s = localStorage.getItem(USER_KEY);
    if (!s?.trim()) return null;
    try {
      return JSON.parse(s) as AuthUser;
    } catch {
      localStorage.removeItem(USER_KEY);
      return null;
    }
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  async logout(): Promise<void> {
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    // Fire server-side token invalidation (best-effort; don't block UI)
    if (refreshToken) {
      try {
        await authFetch('/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
      } catch { /* network failure — still clear local state */ }
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(REATTACH_KEY); // "Abmelden": the next login is a new device (keys are deleted too)
    window.dispatchEvent(new CustomEvent(AUTH_EVENT));
  },

  enableDeviceReattach(guard: (rec: DeviceReattachRecord) => Promise<boolean>): void {
    reattachGuard = guard;
  },

  setTokenFromPortal(accessToken: string, user: AuthUser, refreshToken?: string): void {
    localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    persistAuthUser(user);
    scheduleProactiveRefresh(accessToken);
    window.dispatchEvent(new CustomEvent(AUTH_EVENT));
  },

  /** Load user profile when a token exists but auth_user was never stored (e.g. older logins). */
  async hydrateUser(): Promise<AuthUser | null> {
    if (!localStorage.getItem(TOKEN_KEY)) return null;
    const existing = this.getUser();
    if (existing?._id && existing.username) return existing;
    try {
      const token = this.getToken();
      if (!token) return existing;
      const res = await authFetch('/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await readJsonBody(res);
      if (!res.ok) return existing;
      const user = data.user ?? data;
      persistAuthUser(user);
      window.dispatchEvent(new CustomEvent(AUTH_EVENT));
      return this.getUser();
    } catch {
      return existing;
    }
  },

  async deleteAccount(password: string): Promise<void> {
    const token = this.getToken();
    if (!token) throw new AuthApiError('Not authenticated', 401);
    const res = await authFetch('/account', {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });
    const data = await readJsonBody(res);
    if (!res.ok) {
      throw new AuthApiError(strField(data, 'error', 'Failed to delete account'), res.status);
    }
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    window.dispatchEvent(new CustomEvent(AUTH_EVENT));
  },

  async listDevices() {
    const data = await deviceRequest('/devices', { method: 'GET' }, 'Failed to load devices');
    return {
      devices: Array.isArray(data.devices) ? (data.devices as AuthDevice[]) : [],
      legacySessions: typeof data.legacySessions === 'number' ? data.legacySessions : 0,
      maxDevices: typeof data.maxDevices === 'number' ? data.maxDevices : 10,
    };
  },

  async renameDevice(deviceId: string, name: string) {
    await deviceRequest(`/devices/${encodeURIComponent(deviceId)}`, { method: 'PATCH', body: JSON.stringify({ name }) }, 'Failed to rename device');
  },

  async removeDevice(deviceId: string) {
    await deviceRequest(`/devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' }, 'Failed to remove device');
  },

  async removeOtherDevices() {
    const data = await deviceRequest('/devices?others=1', { method: 'DELETE' }, 'Failed to remove devices');
    return typeof data.removedDevices === 'number' ? data.removedDevices : 0;
  },

  async removeDeviceForLogin(removalToken: string, deviceId: string) {
    const res = await authFetch('/devices/remove-for-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ removalToken, deviceId }),
    });
    const data = await readJsonBody(res);
    if (!res.ok) throw new AuthApiError(strField(data, 'error', 'Failed to remove device'), res.status);
  },

  /**
   * Silently refresh the access token using the stored refresh token.
   * Deduplicates concurrent calls — only one refresh request in flight at a time.
   * Returns the new access token, or null if refresh is not possible.
   */
  async silentRefresh(): Promise<string | null> {
    if (!localStorage.getItem(REFRESH_KEY)) return null;

    // Deduplicate concurrent calls
    if (refreshInFlight) return refreshInFlight;

    const tokenBefore = localStorage.getItem(TOKEN_KEY);
    const attempt = withCrossTabRefreshLock(async () => {
      try {
        // Another tab may have rotated the shared refresh token while this one waited for the lock. Sending the
        // old one again would count as token reuse and sign this device out (report 23), so use theirs instead.
        const latest = localStorage.getItem(TOKEN_KEY);
        if (latest && latest !== tokenBefore && msUntilExpiry(latest) > 60_000) {
          scheduleProactiveRefresh(latest);
          return latest;
        }
        const rt = localStorage.getItem(REFRESH_KEY);
        if (!rt) return null;
        const res = await authFetch('/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: rt }),
        });
        if (!res.ok) {
          // Refresh token invalid or expired — force logout
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_KEY);
          localStorage.removeItem(USER_KEY);
          window.dispatchEvent(new CustomEvent(AUTH_EVENT));
          return null;
        }
        const data = await readJsonBody(res);
        const accessToken = strField(data, 'accessToken', '');
        if (!accessToken) return null;
        localStorage.setItem(TOKEN_KEY, accessToken);
        const newRt = strField(data, 'refreshToken', '');
        if (newRt) localStorage.setItem(REFRESH_KEY, newRt);
        scheduleProactiveRefresh(accessToken);
        window.dispatchEvent(new CustomEvent(AUTH_EVENT));
        return accessToken;
      } catch {
        return null;
      }
    });
    // Cleared only after it settles: the callback can finish synchronously (no await on the early-return path),
    // and clearing inside it would leave a stale promise behind.
    refreshInFlight = attempt;
    void attempt.finally(() => {
      if (refreshInFlight === attempt) refreshInFlight = null;
    });

    return attempt;
  },
};

// ─── On module load: schedule proactive refresh if a token is already stored ──
(function initRefreshSchedule() {
  const existing = localStorage.getItem(TOKEN_KEY);
  if (existing) {
    const ms = msUntilExpiry(existing);
    if (ms > 0) {
      scheduleProactiveRefresh(existing);
    } else if (ms <= 0) {
      // Token already expired — attempt immediate silent refresh
      authService.silentRefresh().catch(() => null);
    }
  }
})();

// ─── Shared apiFetch factory ───────────────────────────────────────────────────

/** The subset of AuthService createApiFetch needs — any real AuthService satisfies this. */
export interface ApiFetchAuth {
  getToken(): string | null;
  silentRefresh(): Promise<string | null>;
}

export interface CreateApiFetchOptions {
  /** API base URL, prepended to every request path (e.g. from resolveSafeBaseUrl). */
  baseUrl: string;
  /** Abort the request after this many ms. Omit to never time out (matches prior per-platform behavior). */
  timeoutMs?: number;
  /** Extra headers merged in before per-call options.headers (e.g. SaaS's client-preview headers). */
  extraHeaders?: () => Record<string, string> | undefined;
}

/**
 * Build an authenticated fetch helper with automatic 401 → silent-refresh → retry-once,
 * replacing the near-identical apiFetch duplicated in each platform's utils/api.ts.
 */
export function createApiFetch(auth: ApiFetchAuth, opts: CreateApiFetchOptions) {
  const { baseUrl, timeoutMs, extraHeaders } = opts;

  return async function apiFetch<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
    const makeRequest = async (token: string | null): Promise<Response> => {
      const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(extraHeaders?.() || {}) };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const controller = timeoutMs ? new AbortController() : undefined;
      const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
      try {
        return await fetch(`${baseUrl}${path}`, {
          ...options,
          headers: { ...headers, ...(options.headers as Record<string, string> | undefined) },
          signal: controller?.signal ?? options.signal,
        });
      } finally {
        if (timer) clearTimeout(timer);
      }
    };

    let res = await makeRequest(auth.getToken());

    // On 401, attempt a silent token refresh and retry once
    if (res.status === 401) {
      const newToken = await auth.silentRefresh();
      if (newToken) {
        res = await makeRequest(newToken);
      }
    }

    const data = await res.json() as T;
    if (!res.ok) throw new Error((data as { error?: string }).error || 'Request failed');
    return data;
  };
}
